import React, { useEffect, useState, useCallback, useRef } from "react";
import { Box, Modal, TextField, Autocomplete } from "@mui/material";
import { GoogleMap, useJsApiLoader } from "@react-google-maps/api";
import useApi from "@common/utils/useApi";
import { haversine } from "../../../../../utils/haversine";
import apiUrl from "@common/utils/apiURL";

const getModalStyle = (isMobile) => ({
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: isMobile ? "95vw" : "90vw",
  height: isMobile ? "95vh" : "90vh",
  maxWidth: isMobile ? "none" : "1200px",
  maxHeight: isMobile ? "none" : "800px",
  backgroundColor: "#ffffff",
  borderRadius: isMobile ? "4px" : "8px",
  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
});

const getContainerStyle = (isMobile) => ({
  width: "100%",
  height: isMobile ? "300px" : "100%",
});

const defaultCenter = {
  lat: 20.5937,
  lng: 78.9629,
};

const libraries = ["places"];
const googleMapsApiKey = "AIzaSyBKLFaOo3qUrrLmnWyjgbgnfcZTTfVNu20";

function LocateStoreModal({ open, onClose }) {
  const { isLoaded } = useJsApiLoader({
    id: "map",
    googleMapsApiKey,
    libraries,
  });

  const [map, setMapInstance] = useState(null);
  const [center, setCenter] = useState(defaultCenter);
  const [centerChangedFlag, setCenterChangedFlag] = useState(false);
  const [zoom, setZoom] = useState(4.8);
  const [storeListData, setStoreListData] = useState([]);
  const [filteredStoreListData, setFilteredStoreListData] = useState([]);
  const [eboTab, setEboTab] = useState(true);
  const [mboTab, setMboTab] = useState(true);
  const categoryType = "FABRIC";
  const infoWindowRef = useRef(null);
  const [places, setPlaces] = useState([]);
  const [selectedPlace, setSelectedPlace] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const isMobile = windowSize.width < 768;
  const isTablet = windowSize.width >= 768 && windowSize.width < 1024;

  const url = apiUrl.store;
  const { data: response, loading: isLoading } = useApi(url);

  useEffect(() => {
    if (!isLoading && response) {
      setStoreListData(response);
      setFilteredStoreListData(response);
    }
  }, [isLoading, response]);

  const onLoad = useCallback((map) => {
    setMapInstance(map);
  }, []);

  const onUnmount = useCallback(() => {
    setMapInstance(null);
  }, []);

  useEffect(() => {
    if (map && storeListData.length > 0) {
      let filteredStores = [];
      if (!centerChangedFlag) {
        filteredStores = storeListData.filter((store) => {
          if (
            eboTab &&
            store?.storeType === "EBO" &&
            store?.categoryType?.includes(categoryType)
          )
            return true;
          if (
            mboTab &&
            store?.storeType === "MBO" &&
            store?.categoryType?.includes(categoryType)
          )
            return true;
          return false;
        });
        setFilteredStoreListData(filteredStores);
      } else {
        let tempStoreList = storeListData.map((item) => {
          const distance = haversine(
            center.lat,
            center.lng,
            item.latitude,
            item.longitude
          );
          return { ...item, distance };
        });
        let tempList = tempStoreList.filter((item) => {
          return (
            item?.distance <= 50 && item?.categoryType?.includes(categoryType)
          );
        });
        filteredStores = tempList.filter((store) => {
          if (eboTab && store.storeType === "EBO") return true;
          if (mboTab && store.storeType === "MBO") return true;
          return false;
        });
      }
      setFilteredStoreListData(filteredStores);

      const markers = filteredStores.map((store) => {
        const svgMBOMarker = {
          path: "M13.2099 0.919922C17.4299 1.00992 20.8499 2.71994 23.4299 6.05994C25.1599 8.29994 26.0099 10.87 25.9599 13.7C25.9199 15.92 25.2699 17.97 24.1599 19.9C21.5999 24.36 18.8899 28.75 16.1999 33.14C15.2699 34.66 14.3299 36.1699 13.3999 37.6799C13.1699 38.0499 13.0899 38.0499 12.8699 37.6799C11.2499 34.9699 9.6099 32.2599 7.9999 29.5399C5.8799 25.9599 3.6999 22.4099 1.7199 18.7499C1.0399 17.4999 0.739904 16.1099 0.599904 14.6899C0.239904 11.0399 1.3199 7.83995 3.7499 5.13995C5.8699 2.77995 8.53989 1.37994 11.7099 0.999939C11.9099 0.979939 12.1199 0.939932 12.3199 0.929932C12.6099 0.919932 12.9099 0.929932 13.1999 0.929932L13.2099 0.919922ZM13.2399 7.10992C9.94989 7.10992 7.33989 9.73997 7.33989 13.03C7.33989 16.3 9.9899 18.9199 13.2799 18.9199C16.5199 18.9199 19.1299 16.2499 19.1599 12.9999C19.1899 9.79994 16.4299 7.00991 13.2299 7.09991L13.2399 7.10992Z",
          fillColor: "#c3a289",
          fillOpacity: 1,
          strokeWeight: 0,
          rotation: 0,
          scale: 0.75,
          anchor: new window.google.maps.Point(13, 40),
        };

        const svgEBOMarker = {
          path: "M13.2099 0.919922C17.4299 1.00992 20.8499 2.71994 23.4299 6.05994C25.1599 8.29994 26.0099 10.87 25.9599 13.7C25.9199 15.92 25.2699 17.97 24.1599 19.9C21.5999 24.36 18.8899 28.75 16.1999 33.14C15.2699 34.66 14.3299 36.1699 13.3999 37.6799C13.1699 38.0499 13.0899 38.0499 12.8699 37.6799C11.2499 34.9699 9.6099 32.2599 7.9999 29.5399C5.8799 25.9599 3.6999 22.4099 1.7199 18.7499C1.0399 17.4999 0.739904 16.1099 0.599904 14.6899C0.239904 11.0399 1.3199 7.83995 3.7499 5.13995C5.8699 2.77995 8.53989 1.37994 11.7099 0.999939C11.9099 0.979939 12.1199 0.939932 12.3199 0.929932C12.6099 0.919932 12.9099 0.929932 13.1999 0.929932L13.2099 0.919922ZM13.2399 7.10992C9.94989 7.10992 7.33989 9.73997 7.33989 13.03C7.33989 16.3 9.9899 18.9199 13.2799 18.9199C16.5199 18.9199 19.1299 16.2499 19.1599 12.9999C19.1899 9.79994 16.4299 7.00991 13.2299 7.09991L13.2399 7.10992Z",
          fillColor: "#0D5640",
          fillOpacity: 1,
          strokeWeight: 0,
          rotation: 0,
          scale: 0.75,
          anchor: new window.google.maps.Point(13, 40),
        };

        const marker = new window.google.maps.Marker({
          position: {
            lat: Number(store.latitude),
            lng: Number(store.longitude),
          },
          title: store.storeName,
          icon: store.storeType === "EBO" ? svgEBOMarker : svgMBOMarker,
        });

        marker.addListener("click", () => {
          if (infoWindowRef.current) {
            infoWindowRef.current.close();
          }

          const newInfoWindow = new window.google.maps.InfoWindow({
            content: `
                            <div class='lyt-locator__info-window'>
                                <div class='bs-info-window'>
                                    <p class='bs-info-window__item-name'>${
                                      store.storeName
                                    }</p>
                                    <address class='bs-info-window__item-address'>
                                        ${`${store.fullAddress} ${store.city} ${store.state} ${store.pinCode}`}
                                    </address>
                                    <p class='bs-info-window__item-time'>Contact - ${
                                      store.contactNumber
                                    }</p>
                                    <a href=${
                                      store.googleMapLink
                                    } target="_blank" class='bs-info-window__item-direction'>
                                        <span class="icon icon-direction bs-info-window__icon"></span>
                                        <span class="bs-info-window__text">Directions</span>
                                    </a>
                                </div>
                            </div>
                        `,
          });

          newInfoWindow.open(map, marker);
          infoWindowRef.current = newInfoWindow;
        });

        marker.setMap(map);
        return marker;
      });

      return () => {
        markers.forEach((marker) => marker.setMap(null));
      };
    }
  }, [
    storeListData,
    map,
    eboTab,
    mboTab,
    center,
    categoryType,
    centerChangedFlag,
  ]);

  const handleDetectLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          setCenter({ lat: latitude, lng: longitude });
          setZoom(10);
          setCenterChangedFlag(true);

          let tempStoreList = storeListData.map((item) => {
            const distance = haversine(
              latitude,
              longitude,
              item.latitude,
              item.longitude
            );
            return { ...item, distance };
          });

          tempStoreList = tempStoreList.filter((item) => {
            const withinDistance = item.distance <= 50;
            const matchEbo = eboTab && item.storeType === "EBO";
            const matchMbo = mboTab && item.storeType === "MBO";

            if (!eboTab && !mboTab) return false;
            if (eboTab && mboTab)
              return withinDistance && (matchEbo || matchMbo);
            if (eboTab) return withinDistance && matchEbo;
            if (mboTab) return withinDistance && matchMbo;
            return false;
          });
          setFilteredStoreListData(tempStoreList);
        },
        (error) => {
          console.error("Error getting location", error);
        }
      );
    } else {
      alert("Geolocation is not supported by this browser.");
    }
  };

  const eboHandleChange = () => {
    setEboTab(!eboTab);
  };

  const mboHandleChange = () => {
    setMboTab(!mboTab);
  };

  const fetchPlaces = (input) => {
    const service = new window.google.maps.places.AutocompleteService();
    const request = {
      input,
      types: ["geocode"],
    };
    service.getPlacePredictions(request, (predictions, status) => {
      if (status === window.google.maps.places.PlacesServiceStatus.OK) {
        setPlaces(predictions);
      } else {
        console.error("Error fetching place predictions:", status);
      }
    });
  };

  const handleInputChange = (_, value) => {
    setSearchInput(value);
    if (value) {
      fetchPlaces(value);
    } else {
      setPlaces([]);
    }
  };

  const handleOptionChange = (_, value) => {
    if (value) {
      setSearchInput(value);
      const selectedPlaceData = places.find(
        (place) => place.description === value
      );
      if (selectedPlaceData && map) {
        const service = new window.google.maps.places.PlacesService(map);
        const request = {
          placeId: selectedPlaceData.place_id,
          fields: ["geometry"],
        };
        service.getDetails(request, (place, status) => {
          if (
            status === window.google.maps.places.PlacesServiceStatus.OK &&
            place.geometry
          ) {
            const { lat, lng } = place.geometry.location;
            setSelectedPlace({ lat: lat(), lng: lng() });
            setPlaces([]);
          } else {
            console.error("Error fetching place details:", status);
          }
        });
      }
    }
  };

  const handleSearch = () => {
    if (selectedPlace && selectedPlace.lat && selectedPlace.lng) {
      // Use selected place from dropdown
      handlePlaceSelect(selectedPlace);
    } else if (searchInput && map) {
      // Search for the typed input
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ address: searchInput }, (results, status) => {
        if (status === "OK" && results[0]) {
          const location = results[0].geometry.location;
          const place = { lat: location.lat(), lng: location.lng() };
          handlePlaceSelect(place);
        } else {
          console.error("Geocoding failed:", status);
          alert("Location not found. Please try a different search term.");
        }
      });
    } else {
      alert("Please enter a location to search.");
    }
  };

  const handlePlaceSelect = (place) => {
    if (place && place.lat && place.lng) {
      setCenter(place);
      setZoom(10);
      setCenterChangedFlag(true);

      let tempStoreList = storeListData.map((item) => {
        const distance = haversine(
          place.lat,
          place.lng,
          item.latitude,
          item.longitude
        );
        return { ...item, distance };
      });

      tempStoreList = tempStoreList.filter((item) => {
        const withinDistance = item.distance <= 50;
        const matchEbo = eboTab && item.storeType === "EBO";
        const matchMbo = mboTab && item.storeType === "MBO";

        if (!eboTab && !mboTab) return false;
        if (eboTab && mboTab) return withinDistance && (matchEbo || matchMbo);
        if (eboTab) return withinDistance && matchEbo;
        if (mboTab) return withinDistance && matchMbo;
        return false;
      });
      setFilteredStoreListData(tempStoreList);
    } else {
      const filteredStores = storeListData.filter((store) => {
        if (
          eboTab &&
          store?.storeType === "EBO" &&
          store?.categoryType?.includes(categoryType)
        )
          return true;
        if (
          mboTab &&
          store?.storeType === "MBO" &&
          store?.categoryType?.includes(categoryType)
        )
          return true;
        return false;
      });
      setFilteredStoreListData(filteredStores);
      setCenter(defaultCenter);
      setZoom(4.8);
      setCenterChangedFlag(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="locate-store-modal"
      className="bs-modal bs-modal--typ-xl"
    >
      <Box className="bs-modal__wrapper" sx={getModalStyle(isMobile)}>
        <div
          className="bs-modal__content"
          style={{
            padding: 0,
            height: "100%",
            overflow: isMobile ? "auto" : "hidden",
          }}
        >
          <button
            className="bs-modal__close"
            onClick={onClose}
            style={{
              position: "absolute",
              top: isMobile ? "12px" : "16px",
              right: isMobile ? "12px" : "16px",
              zIndex: 1000,
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: isMobile ? "6px" : "8px",
              width: isMobile ? "28px" : "32px",
              height: isMobile ? "28px" : "32px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <span
              className="icon icon-simple-close"
              style={{
                backgroundColor: "#000000",
                width: isMobile ? "12px" : "16px",
                height: isMobile ? "12px" : "16px",
                display: "block",
              }}
            ></span>
          </button>

          <div
            className="lyt-locator"
            style={{
              height: isMobile ? "auto" : "100%",
              overflow: "hidden",
              minHeight: isMobile ? "100%" : "auto",
            }}
          >
            <div
              className="lyt-locator__head-wrap"
              style={{
                padding: isMobile ? "15px" : "20px",
                borderBottom: "1px solid #eee",
                flexShrink: 0,
              }}
            >
              <h2
                className="bs-section__title"
                style={{
                  marginBottom: isMobile ? "0px" : "20px",
                  marginTop: isMobile ? "8px" : "0",
                  marginRight: isMobile ? "40px" : "0px",
                  textAlign: "left",
                  fontSize: isMobile ? "25px" : "30px",
                  lineHeight: "1.2",
                  fontWeight: "bold",
                  width: isMobile ? "-webkit-fill-available" : "",
                }}
              >
                Find Stores
                <br />
                Near You
              </h2>

              <div className="lyt-locator__search-field-wrap">
                <label
                  className="lyt-locator__search-label"
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontWeight: "bold",
                    fontSize: isMobile ? "13px" : "14px",
                  }}
                >
                  Find the nearest store
                </label>

                <div
                  className="lyt-locator__search-and-actions"
                  style={{
                    display: "flex",
                    flexDirection: isMobile ? "column" : "row",
                    gap: isMobile ? "15px" : "12px",
                    alignItems: isMobile ? "stretch" : "flex-end",
                  }}
                >
                  <div
                    className="lyt-locator__search-field"
                    style={{
                      flex: isMobile ? "none" : "1",
                      marginBottom: isMobile ? "0" : "0",
                    }}
                  >
                    <Autocomplete
                      freeSolo
                      blurOnSelect
                      disablePortal
                      onInputChange={handleInputChange}
                      onChange={handleOptionChange}
                      options={places.map((place) => place.description)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Area / Locality / Pincode"
                          className="lyt-locator__search-input"
                          fullWidth
                          size={isMobile ? "small" : "small"}
                        />
                      )}
                    />
                  </div>

                  <div
                    className="lyt-locator__action-wrap"
                    style={{
                      display: "flex",
                      gap: isMobile ? "8px" : "10px",
                      alignItems: "center",
                      justifyContent: isMobile ? "center" : "flex-start",
                      flexWrap: "wrap",
                      flexShrink: 0,
                    }}
                  >
                    <button
                      className="bs-btn bs-btn__btn-solid lyt-locator__btn"
                      onClick={() => handleSearch()}
                      style={{
                        minWidth: isMobile ? "80px" : "100px",
                        fontSize: isMobile ? "13px" : "14px",
                        padding: isMobile ? "8px 12px" : "10px 16px",
                      }}
                    >
                      Search
                    </button>
                    <span
                      className="lyt-locator__separator"
                      style={{
                        margin: "0 5px",
                        fontSize: isMobile ? "13px" : "14px",
                      }}
                    >
                      or
                    </span>
                    <button
                      className="bs-btn bs-btn__btn-solid lyt-locator__btn"
                      onClick={handleDetectLocation}
                      style={{
                        minWidth: isMobile ? "120px" : "140px",
                        fontSize: isMobile ? "13px" : "14px",
                        padding: isMobile ? "8px 12px" : "10px 16px",
                      }}
                    >
                      Detect my location
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div
              className="lyt-locator__cont-wrap"
              style={{
                height: isMobile ? "auto" : "calc(100% - 200px)",
                display: "flex",
                overflow: "hidden",
                flexDirection: isMobile ? "column" : "row",
                gap: isMobile ? "15px" : "10px",
              }}
            >
              <div
                className="lyt-locator__map-wrap"
                style={{
                  flex: isMobile ? "none" : 1,
                  width: isMobile ? "calc(100% - 40px)" : "auto",
                  position: "relative",
                  minHeight: isMobile ? "300px" : "auto",
                  height: isMobile ? "300px" : "auto",
                  margin: isMobile ? "0 20px" : "0",
                  borderRadius: isMobile ? "8px" : "0",
                  overflow: isMobile ? "hidden" : "visible",
                }}
              >
                {isLoaded ? (
                  <GoogleMap
                    mapContainerStyle={getContainerStyle(isMobile)}
                    center={center}
                    zoom={zoom}
                    onLoad={onLoad}
                    onUnmount={onUnmount}
                    mapId="570347ebde0a874f"
                  />
                ) : (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      height: "100%",
                      fontSize: "16px",
                      color: "#666",
                    }}
                  >
                    Loading map...
                  </div>
                )}

                <div
                  className="lyt-locator__legend-wrap"
                  style={{
                    position: "absolute",
                    bottom: isMobile ? "10px" : "20px",
                    left: isMobile ? "10px" : "20px",
                    background: "white",
                    padding: isMobile ? "8px" : "12px",
                    borderRadius: isMobile ? "6px" : "8px",
                    boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
                    fontSize: isMobile ? "12px" : "14px",
                    maxWidth: isMobile ? "calc(100% - 20px)" : "auto",
                  }}
                >
                  <p
                    className={`lyt-locator__legend lyt-locator__legend--first ${
                      eboTab ? "lyt-locator__legend--first--active" : ""
                    }`}
                    onClick={eboHandleChange}
                    style={{
                      cursor: "pointer",
                      margin: isMobile ? "3px 0" : "5px 0",
                      display: "flex",
                      alignItems: "center",
                      gap: isMobile ? "6px" : "8px",
                    }}
                  >
                    <span className="icon icon-pin lyt-locator__legend-icon lyt-locator__legend-icon--clr-orange"></span>
                    <span className="lyt-locator__legend-text">
                      {isMobile ? "Exclusive" : "Exclusive Store"}
                    </span>
                    <span className="icon icon-circle-empty lyt-locator__legend-check-icon"></span>
                  </p>
                  <p
                    className={`lyt-locator__legend lyt-locator__legend--second ${
                      mboTab ? "lyt-locator__legend--second--active" : ""
                    }`}
                    onClick={mboHandleChange}
                    style={{
                      cursor: "pointer",
                      margin: isMobile ? "3px 0" : "5px 0",
                      display: "flex",
                      alignItems: "center",
                      gap: isMobile ? "6px" : "8px",
                    }}
                  >
                    <span className="icon icon-pin lyt-locator__legend-icon"></span>
                    <span className="lyt-locator__legend-text">
                      {isMobile ? "Multibrand" : "Multibrand Stores"}
                    </span>
                    <span className="icon icon-circle-empty lyt-locator__legend-check-icon"></span>
                  </p>
                </div>
              </div>

              <div
                className="lyt-locator__result-wrap"
                style={{
                  width: isMobile ? "100%" : isTablet ? "350px" : "400px",
                  maxWidth: isMobile ? "none" : isTablet ? "350px" : "400px",
                  overflowY: isMobile ? "visible" : "auto",
                  borderLeft: isMobile ? "none" : "1px solid #eee",
                  borderTop: isMobile ? "1px solid #eee" : "none",
                  padding: isMobile ? "15px" : "20px",
                  height: isMobile ? "auto" : "100%",
                  backgroundColor: "#fafafa",
                  flexShrink: 0,
                  minHeight: isMobile ? "400px" : "auto",
                }}
              >
                <h3
                  style={{
                    marginBottom: isMobile ? "12px" : "15px",
                    fontSize: isMobile ? "16px" : "18px",
                    fontWeight: "bold",
                    color: "#333",
                  }}
                >
                  Stores ({filteredStoreListData.length})
                </h3>
                {filteredStoreListData.map((store, index) => (
                  <div
                    key={index}
                    className="store-item"
                    style={{
                      marginBottom: isMobile ? "12px" : "15px",
                      padding: isMobile ? "12px" : "15px",
                      backgroundColor: "white",
                      border: "1px solid #e0e0e0",
                      borderRadius: isMobile ? "6px" : "8px",
                      boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                      transition: "box-shadow 0.2s ease",
                    }}
                  >
                    <h4
                      style={{
                        margin: "0 0 6px 0",
                        fontSize: isMobile ? "14px" : "16px",
                        fontWeight: "bold",
                        color: "#333",
                        lineHeight: "1.3",
                      }}
                    >
                      {store.storeName}
                    </h4>
                    <p
                      style={{
                        margin: "0 0 6px 0",
                        fontSize: isMobile ? "12px" : "13px",
                        color: "#666",
                        lineHeight: "1.4",
                      }}
                    >
                      {store.fullAddress}, {store.city}, {store.state}{" "}
                      {store.pinCode}
                    </p>
                    <p
                      style={{
                        margin: "0 0 8px 0",
                        fontSize: isMobile ? "12px" : "13px",
                        color: "#555",
                      }}
                    >
                      Contact: {store.contactNumber}
                    </p>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        flexWrap: "wrap",
                        gap: "6px",
                        marginBottom: "8px",
                      }}
                    >
                      <span
                        style={{
                          fontSize: isMobile ? "10px" : "11px",
                          padding: isMobile ? "3px 6px" : "4px 8px",
                          backgroundColor:
                            store.storeType === "EBO" ? "#0D5640" : "#c3a289",
                          color: "white",
                          borderRadius: "4px",
                          fontWeight: "500",
                        }}
                      >
                        {store.storeType === "EBO"
                          ? isMobile
                            ? "Exclusive"
                            : "Exclusive Store"
                          : isMobile
                          ? "Multibrand"
                          : "Multibrand Store"}
                      </span>
                      {store.distance && (
                        <span
                          style={{
                            fontSize: isMobile ? "11px" : "12px",
                            color: "#888",
                            fontWeight: "500",
                          }}
                        >
                          {store.distance.toFixed(1)} km away
                        </span>
                      )}
                    </div>
                    <div
                      style={{
                        display: "flex",
                        gap: isMobile ? "8px" : "10px",
                        alignItems: "center",
                        flexWrap: "wrap",
                      }}
                    >
                      <a
                        href={store.googleMapLink}
                        target="_blank"
                        rel="noreferrer"
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                          textDecoration: "none",
                          color: "#513117",
                          fontSize: isMobile ? "11px" : "12px",
                          fontWeight: "500",
                          padding: isMobile ? "4px 8px" : "6px 10px",
                          border: "1px solid #0D5640",
                          borderRadius: "4px",
                          transition: "all 0.2s ease",
                          backgroundColor: "transparent",
                        }}
                      >
                        <span
                          className="icon icon-direction"
                          style={{
                            width: isMobile ? "12px" : "14px",
                            height: isMobile ? "12px" : "14px",
                            display: "inline-block",
                          }}
                        ></span>
                        <span>Directions</span>
                      </a>
                      <a
                        href={`tel:${store.contactNumber}`}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                          textDecoration: "none",
                          color: "#666",
                          fontSize: isMobile ? "11px" : "12px",
                          fontWeight: "500",
                          padding: isMobile ? "4px 8px" : "6px 10px",
                          border: "1px solid #ccc",
                          borderRadius: "4px",
                          transition: "all 0.2s ease",
                          backgroundColor: "transparent",
                        }}
                      >
                        <span
                          className="icon icon-call"
                          style={{
                            width: isMobile ? "12px" : "14px",
                            height: isMobile ? "12px" : "14px",
                            display: "inline-block",
                          }}
                        ></span>
                        <span>Call</span>
                      </a>
                    </div>
                  </div>
                ))}
                {filteredStoreListData.length === 0 && (
                  <div
                    style={{
                      textAlign: "center",
                      color: "#666",
                      marginTop: isMobile ? "30px" : "50px",
                      padding: isMobile ? "15px" : "20px",
                    }}
                  >
                    <p
                      style={{
                        fontSize: isMobile ? "14px" : "16px",
                        marginBottom: "10px",
                      }}
                    >
                      No stores found in your area
                    </p>
                    <p style={{ fontSize: isMobile ? "12px" : "14px" }}>
                      Try adjusting your location or filters
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </Box>
    </Modal>
  );
}

export default LocateStoreModal;
